import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Callout, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { collectDiagnostics, recordBreadcrumb } from '../../lib/diagnostics.js';
import './feedback.css';

/**
 * "Something's wrong" / "I wish it did…" — one button, two paths.
 *
 * Placed in the top bar rather than floating in a screen corner. The register
 * pins its primary action bottom-right and its destructive action bottom-left
 * (§2.2-1, Fitts), and a floating button in either corner would sit next to
 * Cancel Sale on a busy counter. The top bar is always visible, never moves,
 * and competes with nothing.
 *
 * Two decisions and one sentence is the whole form. Anything longer and it gets
 * used once and then never again, which is the same as not having it.
 */

type Kind = 'problem' | 'feature';
type Severity = 'blocking' | 'annoying' | 'cosmetic';

const SEVERITIES: ReadonlyArray<{ value: Severity; label: string; hint: string }> = [
  { value: 'blocking', label: "Can't continue", hint: 'Stopped me serving a customer' },
  { value: 'annoying', label: 'Wrong, but I coped', hint: 'I worked around it' },
  { value: 'cosmetic', label: 'Looks wrong', hint: 'Confusing or untidy' },
];

export interface FeedbackContext {
  readonly view: string;
  readonly registerId?: string | null;
  readonly saleId?: string | null;
}

export function FeedbackButton({ context }: { context: FeedbackContext }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind | null>(null);
  const [severity, setSeverity] = useState<Severity>('annoying');
  const [summary, setSummary] = useState('');
  const [reference, setReference] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => {
      const diagnostics = collectDiagnostics();
      return api.feedback.submit.mutate({
        kind: kind ?? 'problem',
        severity: kind === 'problem' ? severity : null,
        summary: summary.trim(),
        view: context.view,
        registerId: context.registerId ?? null,
        saleId: context.saleId ?? null,
        ...diagnostics,
      });
    },
    onSuccess: (result) => setReference(result.reference),
  });

  const reset = () => {
    setOpen(false);
    setKind(null);
    setSeverity('annoying');
    setSummary('');
    setReference(null);
    submit.reset();
  };

  // Capture the state of the screen at the moment they reached for the button,
  // not after they have typed for a minute.
  useEffect(() => {
    if (open) recordBreadcrumb('feedback', `opened on ${context.view}`);
  }, [open, context.view]);

  const blocked =
    kind === null
      ? 'Choose whether something is wrong or you are asking for something'
      : summary.trim().length < 3
        ? 'Describe it in a few words'
        : undefined;

  return (
    <>
      {/*
        "Feedback", not "Report a problem".

        §19.1 opens with two choices — problem or request — and a button that
        names only the first will never collect the second. §19.4 makes the
        queue behind it the source for the Phase 2+ backlog, so the asks are
        half the point; nobody presses "Report a problem" to say they wish it
        searched customers by phone number.

        No icon. The top bar is the most contested space in the app — five
        sections plus the utility cluster — and a decorative glyph in front of a
        label that already says exactly what it does buys nothing but width
        (§2.1: colour and ornament are never decoration).
      */}
      <button type="button" className="feedback-trigger" onClick={() => setOpen(true)}>
        Feedback
      </button>

      <Modal
        open={open}
        title={reference !== null ? 'Thank you' : 'What would you like to tell us?'}
        description={
          reference !== null
            ? undefined
            : 'We capture the screen you are on and what the app was doing, so you only need the short version.'
        }
        onClose={reset}
        primaryAction={
          reference !== null ? (
            <Button variant="primary" onClick={reset}>
              Back to work
            </Button>
          ) : (
            <Button
              variant="primary"
              busy={submit.isPending}
              disabledReason={blocked}
              onClick={() => submit.mutate()}
            >
              Send
            </Button>
          )
        }
        secondaryAction={
          reference !== null ? null : (
            <Button variant="ghost" onClick={reset}>
              Cancel
            </Button>
          )
        }
      >
        {reference !== null ? (
          <Callout tone="success" title={`Logged as ${reference}`}>
            {kind === 'problem'
              ? 'We have what we need to look into it. Someone will follow up on this reference.'
              : 'It will be reviewed, and you will get an answer either way — including if we decide not to build it.'}
          </Callout>
        ) : (
          <>
            <div className="feedback-kinds">
              <button
                type="button"
                className={`feedback-kind${kind === 'problem' ? ' feedback-kind--selected' : ''}`}
                onClick={() => setKind('problem')}
              >
                <span className="feedback-kind__label">Something is wrong</span>
                <span className="text-secondary">An error, or it did the wrong thing</span>
              </button>
              <button
                type="button"
                className={`feedback-kind${kind === 'feature' ? ' feedback-kind--selected' : ''}`}
                onClick={() => setKind('feature')}
              >
                <span className="feedback-kind__label">I need it to do something</span>
                <span className="text-secondary">A change or an addition</span>
              </button>
            </div>

            {kind === 'problem' ? (
              <fieldset className="feedback-severity">
                <legend className="field__label">How bad was it?</legend>
                {SEVERITIES.map((option) => (
                  <label key={option.value} className="feedback-severity__option">
                    <input
                      type="radio"
                      name="severity"
                      checked={severity === option.value}
                      onChange={() => setSeverity(option.value)}
                    />
                    <span>
                      {option.label}
                      <span className="text-secondary"> — {option.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            ) : null}

            {kind !== null ? (
              <>
                {/*
                  One box, and it is required.
                  *
                  A summary line plus an optional "anything else" made a report
                  a small form, and an optional field on a form staff fill in
                  mid-shift is a field that is left empty — so it cost every
                  reporter a decision and bought nothing. What matters is that
                  the thing gets written down at all, and the screen, the view
                  and the last few actions are attached automatically anyway.
                */}
                <label className="field__label" htmlFor="feedback-summary">
                  {kind === 'problem' ? 'What happened?' : 'What do you need?'}
                </label>
                <textarea
                  id="feedback-summary"
                  className="input feedback-detail"
                  autoFocus
                  rows={5}
                  maxLength={500}
                  placeholder={
                    kind === 'problem'
                      ? 'The cart emptied when I came back from Purchasing'
                      : 'Let me search customers by phone number'
                  }
                  value={summary}
                  onChange={(event) => setSummary(event.target.value)}
                />
              </>
            ) : null}

            {submit.isError ? <Callout tone="danger">{errorMessage(submit.error)}</Callout> : null}
          </>
        )}
      </Modal>
    </>
  );
}
